import { useCallback, useEffect, useState } from 'react';
import { BOARD, parseRoute, pathForRoute, sameRoute, type Route } from './route';

/**
 * Keeps what is on screen and the address bar as one thing.
 *
 * Every open and close is a history entry, so Back and Forward move between the board, an
 * epic and a ticket for free — which is what people try first, and what a hand-rolled
 * route most often gets wrong by only writing the URL one way.
 */
export function useAppRoute() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  /**
   * Whether this page load ARRIVED at a ticket, rather than opening one later. Read once
   * and kept, because it decides whether the drawer opens expanded: a pasted link should
   * land on the ticket itself, while clicking a card should keep the drawer it has
   * always had.
   */
  const [arrivedAtTicket] = useState(() => parseRoute(window.location.pathname).kind === 'ticket');

  /**
   * What closing a ticket returns to.
   *
   * A ticket opens OVER something — the board, or an epic page — and closing it has to
   * put that back. Hard-coding the board here is the bug: opening a ticket from an epic
   * and closing it would silently drop you somewhere you never navigated to, and the
   * epic page is exactly where the ticket was worth opening from.
   */
  const [base, setBase] = useState<Route>(() =>
    parseRoute(window.location.pathname).kind === 'ticket'
      ? BOARD
      : parseRoute(window.location.pathname),
  );

  useEffect(() => {
    // The address bar is authoritative on Back/Forward — read it rather than trying to
    // track our own position in the history stack.
    const onPop = () => {
      const next = parseRoute(window.location.pathname);
      if (next.kind !== 'ticket') setBase(next);
      setRoute(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route) => {
    if (next.kind !== 'ticket') setBase(next);
    setRoute(next);

    const path = pathForRoute(next);
    // Load-bearing beyond the obvious: this is the idempotence `BoardControlsInput.navigate`
    // requires, and that field explains why.
    if (window.location.pathname === path) return;
    // Compared by ROUTE, not by string: `/DWLF-12/` is a form the parser deliberately
    // accepts, so arriving at it and clicking that same ticket's card would otherwise
    // stack an entry Back has to walk through — the case this guard exists for. Same
    // thing, non-canonical path: rewrite it rather than push.
    const same = sameRoute(parseRoute(window.location.pathname), next);
    window.history[same ? 'replaceState' : 'pushState'](null, '', path);
  }, []);

  const openTicket = useCallback((ref: number) => navigate({ kind: 'ticket', ref }), [navigate]);

  /** Back to whatever the ticket was opened over. */
  const closeTicket = useCallback(() => navigate(base), [navigate, base]);

  return { route, base, navigate, openTicket, closeTicket, arrivedAtTicket };
}
